import React, { useContext } from 'react';
import AddIcon from '@material-ui/icons/Add';
import {
  DRAWER_REGISTRY_MAP,
  DrawerStack,
  SendoutsDrawerStackProvider,
  useSendoutsDrawerHandlers
} from 'features/sendouts';
import { UserProviderContext } from 'providers/UserProvider';
import { canUserOrStaffEditEntity } from 'services/Authorization';
import strings from 'strings';
import { componentDimensions } from 'UI/constants/dimensions';
import { EntityType, TabKeys } from 'UI/constants/entityTypes';
import { SvgNoSendouts } from 'UI/res/icons/milano';

import ProfileTableLayout from '../ProfileTableLayout';

const {
  sendouts: STRINGS,
  inventoryProfiles: {
    emptyStates: { sendouts: sendoutsEmptyStrings }
  }
} = strings;

const { emptyState } = componentDimensions;

const PROCESS_TYPE = {
  Sendout: 'Sendout',
  Sendover: 'Sendover'
};

const SendoutsTabContent = ({
  profile,
  onNewItemClick,
  onSendoutClick,
  profileModule,
  shouldRefresh,
  DataTableProps,
  initialColumns,
  extraRenderers,
  paramsBuilder,
  tablePreferencesKey,
  canUseSendout,
  drawerHandlers,
  ...rest
}) => {
  const {
    stack,
    openCreateSendout,
    openCreateSendover,
    onDrawerClose,
    onDrawerSaved,
    openSendoutDetails,
    openSendoverDetails
  } = drawerHandlers || {};
  const isJobOrderMode = profileModule === EntityType.Joborder;

  const handleViewClick = item => {
    if (!item || !item.type) return;

    if (item.type?.title === PROCESS_TYPE.Sendout) {
      openSendoutDetails({ sendoutId: item.id });
    } else if (item.type?.title === PROCESS_TYPE.Sendover) {
      openSendoverDetails({ sendoverId: item.id });
    }
  };

  const actionButtonProps = { disabled: !canUseSendout || !profile?.id };
  const finalActions = [
    {
      name: 'view',
      callback: sendout => handleViewClick(sendout)
    }
  ];

  const accountable = profile?.recruiter || null;
  const collaborator = profile?.additionalRecruiters?.[0] || null;

  const actionsDefinition = [
    {
      props: {
        text: STRINGS.tab.actions.createSendout,
        variant: 'contained',
        onClick: () =>
          openCreateSendout({
            accountableId: accountable?.id,
            collaboratorId: collaborator?.recruiter_id
          }),
        icon: AddIcon,
        disabled: !canUseSendout || !profile?.id
      },
      GridItemProps: {
        md: 'auto'
      }
    },
    {
      props: {
        text: STRINGS.tab.actions.createSendover,
        variant: 'outlined',
        onClick: () => openCreateSendover({ accountableId: accountable?.id }),
        icon: AddIcon,
        disabled: !canUseSendout || !profile?.id
      },
      GridItemProps: {
        md: 'auto'
      }
    }
  ];

  return (
    <>
      <ProfileTableLayout
        actionButtonProps={actionButtonProps}
        actionText={STRINGS.tab.actions.createSendout}
        customEmptyState={<SvgNoSendouts size={emptyState.width} />}
        customActionsDefinitions={actionsDefinition}
        DataTableProps={DataTableProps}
        extraRenderers={extraRenderers}
        finalActions={finalActions}
        hasProfileLoaded={profile?.id}
        initialColumns={initialColumns}
        onNewItemClick={isJobOrderMode ? onNewItemClick : null}
        profileModule={profileModule}
        shouldRefresh={shouldRefresh}
        tabKey={TabKeys.Sendouts}
        paramsBuilder={paramsBuilder}
        orderByOptions={{ column: 'sent_on', direction: 'desc' }}
        tablePreferencesKey={tablePreferencesKey}
        {...sendoutsEmptyStrings}
        {...rest}
      />
      {isJobOrderMode && stack && (
        <DrawerStack
          stack={stack}
          registry={DRAWER_REGISTRY_MAP}
          onClose={onDrawerClose}
          onSaved={onDrawerSaved}
        />
      )}
    </>
  );
};

const SendoutsTabWrapper = props => {
  const { profile } = props;
  const drawerHandlers = useSendoutsDrawerHandlers({ jobOrder: profile });

  return <SendoutsTabContent {...props} drawerHandlers={drawerHandlers} />;
};

const SendoutsTabV2 = props => {
  const { profile, profileModule } = props;
  const [currentUser] = useContext(UserProviderContext);
  const canUseSendout = canUserOrStaffEditEntity(currentUser, profile);
  const isJobOrderMode = profileModule === EntityType.Joborder;

  if (!isJobOrderMode) {
    return <SendoutsTabContent {...props} canUseSendout={canUseSendout} />;
  }

  return (
    <SendoutsDrawerStackProvider>
      <SendoutsTabWrapper {...props} canUseSendout={canUseSendout} />
    </SendoutsDrawerStackProvider>
  );
};

export default SendoutsTabV2;
